/**
 * Scan progress component.
 *
 * Translates raw pipeline execution stages into a calm, human-readable
 * 5-phase progress display. Progress never decreases (monotonic).
 *
 * Phases:
 *   1. Preparing scan        (10%)  — job accepted / queued
 *   2. Finding key pages     (20%)  — discovery, canon, classification, templates
 *   3. Checking accessibility(45%)  — renders, scans, screenshots, documents (if enabled)
 *   4. Building results      (20%)  — normalisations, scores, reports, statements, recurring
 *   5. Finishing              (5%)  — final persistence / completion
 */

import { useState, useEffect, useRef } from "react";
import type { PipelineExecutionSummary } from "../api/pipeline.js";
import { RunStatusBadge } from "./run-status-badge.js";

/* ── Phase definitions ───────────────────────────────────────────────────── */

interface Phase {
  label: string;
  description: string;
  /** Percentage range: [start, end) */
  start: number;
  end: number;
  /** Backend stage names that belong to this phase */
  stages: string[];
}

const PHASES: Phase[] = [
  {
    label: "Preparing scan",
    description: "Setting up the scan environment",
    start: 0,
    end: 10,
    stages: [],
  },
  {
    label: "Finding key pages",
    description: "Discovering and grouping pages by template",
    start: 10,
    end: 30,
    stages: ["discovery", "canon", "classification", "templates"],
  },
  {
    label: "Checking accessibility",
    description: "Rendering representative pages and running accessibility checks — this is usually the longest step",
    start: 30,
    end: 75,
    stages: ["renders", "scans", "screenshots", "documents"],
  },
  {
    label: "Building results",
    description: "Consolidating findings and calculating your score",
    start: 75,
    end: 95,
    stages: ["normalisations", "scores", "reports", "statements", "recurring"],
  },
  {
    label: "Finishing",
    description: "Finalising your score and statement",
    start: 95,
    end: 100,
    stages: [],
  },
];

const STAGE_LABELS: Record<string, string> = {
  discovery: "Discover pages",
  canon: "Resolve URLs",
  classification: "Classify pages",
  templates: "Detect page templates",
  renders: "Load representative pages",
  scans: "Run accessibility checks on each page",
  normalisations: "Consolidate findings",
  screenshots: "Capture visual evidence",
  scores: "Calculate score",
  reports: "Build report",
  statements: "Generate accessibility statement",
  documents: "Scan documents",
  recurring: "Compare with previous scan",
};

/* ── Progress calculation ────────────────────────────────────────────────── */

const DONE_STATUSES = new Set(["success", "failed", "skipped", "partial"]);

function calculateProgress(execution: PipelineExecutionSummary | null): {
  percent: number;
  phaseIndex: number;
} {
  if (!execution) return { percent: 0, phaseIndex: 0 };
  if (execution.status !== "running") return { percent: 100, phaseIndex: 4 };

  const stages = execution.stages;
  if (stages.length === 0) return { percent: 5, phaseIndex: 0 };

  // Walk through phases, find the active one
  for (let pi = PHASES.length - 1; pi >= 0; pi--) {
    const phase = PHASES[pi]!;
    if (phase.stages.length === 0) continue;

    const phaseStages = stages.filter((s) => phase.stages.includes(s.stage));
    if (phaseStages.length === 0) continue;

    const doneCount = phaseStages.filter((s) =>
      DONE_STATUSES.has(s.status),
    ).length;
    const hasRunning = phaseStages.some((s) => s.status === "running");

    if (doneCount === phaseStages.length) {
      // This phase is fully complete — progress is at least the end of this phase
      return { percent: phase.end, phaseIndex: Math.min(pi + 1, 4) };
    }

    if (doneCount > 0 || hasRunning) {
      // Partially through this phase
      const fraction =
        phase.stages.length > 0
          ? (doneCount + (hasRunning ? 0.5 : 0)) / phase.stages.length
          : 0;
      const pct = phase.start + fraction * (phase.end - phase.start);
      return { percent: Math.round(pct), phaseIndex: pi };
    }
  }

  // No stages have started yet — still preparing
  return { percent: 5, phaseIndex: 0 };
}

/* ── Elapsed time formatter ──────────────────────────────────────────────── */

function formatElapsed(startedAt: string): string {
  const diff = Date.now() - new Date(startedAt).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `Started ${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `Started ${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `Started ${hours}h ${minutes % 60}m ago`;
}

/* ── Component ───────────────────────────────────────────────────────────── */

interface ScanProgressProps {
  execution: PipelineExecutionSummary;
}

export function ScanProgress({ execution }: ScanProgressProps) {
  const { percent, phaseIndex } = calculateProgress(execution);
  const phase = PHASES[phaseIndex]!;

  // Monotonic progress — never let displayed progress decrease
  const highWaterMark = useRef(0);
  if (percent > highWaterMark.current) {
    highWaterMark.current = percent;
  }
  const displayPercent = highWaterMark.current;

  // Reset high-water mark when execution changes
  const executionIdRef = useRef(execution.id);
  if (execution.id !== executionIdRef.current) {
    executionIdRef.current = execution.id;
    highWaterMark.current = percent;
  }

  // Tick timer for elapsed time display
  const [, setTick] = useState(0);
  useEffect(() => {
    if (execution.status !== "running") return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [execution.status]);

  const isComplete =
    execution.status === "success" || execution.status === "partial";
  const isFailed = execution.status === "failed";

  return (
    <div className="scan-progress">
      {/* ── Title ──────────────────────────────────────────────────────── */}
      <div className="scan-progress__header">
        <h3 className="scan-progress__title">
          {isComplete
            ? "Scan complete"
            : isFailed
              ? "Scan failed"
              : "Scan in progress"}
        </h3>
        {execution.status === "running" && (
          <span className="scan-progress__elapsed text-muted text-sm">
            {formatElapsed(execution.startedAt)}
          </span>
        )}
      </div>

      {/* ── Progress bar ───────────────────────────────────────────────── */}
      <div
        className="scan-progress__track"
        role="progressbar"
        aria-valuenow={displayPercent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Scan progress: ${displayPercent}%`}
      >
        <div
          className={`scan-progress__fill${isFailed ? " scan-progress__fill--error" : ""}${isComplete ? " scan-progress__fill--done" : ""}`}
          style={{ width: `${displayPercent}%` }}
        />
      </div>

      {/* ── Phase label + description ──────────────────────────────────── */}
      {execution.status === "running" && (
        <div className="scan-progress__phase">
          <span className="scan-progress__phase-label">{phase.label}</span>
          <span className="scan-progress__phase-desc text-muted text-sm">
            {phase.description}
          </span>
        </div>
      )}

      {/* ── Failed message ─────────────────────────────────────────────── */}
      {isFailed && (
        <div className="alert alert--error mt-2">
          The scan encountered an error. You can try running it again.
          {execution.stages
            .filter((s) => s.status === "failed")
            .map((s) => (
              <p
                key={s.stage}
                className="text-sm"
                style={{ margin: "4px 0 0" }}
              >
                Stage failed: {STAGE_LABELS[s.stage] ?? s.stage}
              </p>
            ))}
        </div>
      )}

      {/* ── Technical detail (collapsible) ─────────────────────────────── */}
      {execution.stages.length > 0 && (
        <details
          className="scan-progress__details mt-3"
        >
          <summary className="scan-progress__details-toggle">
            View technical detail
          </summary>
          <div className="scan-progress__stages mt-2">
            {execution.stages.map((s) => (
              <div key={s.stage} className="scan-progress__stage-row">
                <span className="scan-progress__stage-name text-sm">
                  {STAGE_LABELS[s.stage] ?? s.stage}
                </span>
                <RunStatusBadge
                  status={
                    s.status as
                      | "success"
                      | "partial"
                      | "failed"
                      | "running"
                      | "idle"
                      | "skipped"
                  }
                />
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
